/**
 * Tenant Comms Automations Tab V2
 * 
 * V2 Features:
 * - Preview Qualification (count + 10 sample recipients with match reasons)
 * - Per-automation Test Now (sends ONLY to preview email - safe sandbox)
 * - Constrained editable fields (no freeform rule builder)
 * - Searchable tenant dropdown for seeding (no raw ID input)
 * - Dark mode visibility fixes
 * 
 * SAFETY:
 * - Uses existing send_email_via_ledger pathway
 * - All emails appear in Admin Communications Log
 * - Deploy dark and safe
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Robot,
  Play,
  PauseCircle,
  Gear,
  ArrowClockwise,
  Warning,
  CheckCircle,
  Clock,
  Envelope,
  Lightning,
  Eye,
  PencilSimple,
  MagnifyingGlass,
  Users,
  X,
  FileText,
  ArrowSquareOut,
  Info,
  Wrench,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper for auth fetch
const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });
};

// ============================================================================
// AUTOMATIONS TAB V2 COMPONENT
// ============================================================================

export function AutomationsTab() {
  const navigate = useNavigate();
  
  // Global settings state
  const [globalSettings, setGlobalSettings] = useState({
    enabled_global: false,
    preview_recipient_email: "",
  });
  
  // Platform defaults state (PRIMARY VIEW)
  const [platformDefaults, setPlatformDefaults] = useState([]);
  const [loadingDefaults, setLoadingDefaults] = useState(true);
  
  // Tenant instances state (SECONDARY VIEW)
  const [tenantInstances, setTenantInstances] = useState([]);
  const [instancesTotal, setInstancesTotal] = useState(0);
  const [instancesPage, setInstancesPage] = useState(1);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [showTenantInstances, setShowTenantInstances] = useState(false);
  
  // Legacy automations list state (for backwards compat)
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningCycle, setRunningCycle] = useState(false);
  
  // V2 Enums (from backend)
  const [v2Enums, setV2Enums] = useState(null);
  
  // Seed dialog state - V2 platform-level seeding (no tenant selection)
  const [seedDialog, setSeedDialog] = useState(false);
  const [seeding, setSeeding] = useState(false);
  
  // Edit dialog state - V2 with constrained fields
  const [editDialog, setEditDialog] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [isPlatformDefault, setIsPlatformDefault] = useState(false);
  
  // Preview dialog state - V2 qualification preview
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Test now state
  const [testingAutomation, setTestingAutomation] = useState(null);
  const [testResult, setTestResult] = useState(null);

  // Template selection state
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templatePreviewData, setTemplatePreviewData] = useState(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchPlatformDefaults();
    fetchGlobalSettings();
    fetchV2Enums();
    fetchAvailableTemplates();
  }, []);

  const fetchPlatformDefaults = async () => {
    setLoadingDefaults(true);
    try {
      const res = await authFetch(`${API}/admin/tenant-automations/v2/platform-defaults`);
      if (res.ok) {
        const data = await res.json();
        setPlatformDefaults(data.platform_defaults || []);
      }
    } catch (err) {
      console.error("Failed to fetch platform defaults:", err);
    } finally {
      setLoadingDefaults(false);
    }
  };

  const fetchGlobalSettings = async () => {
    try {
      const res = await authFetch(`${API}/admin/tenant-automations/settings`);
      if (res.ok) {
        const data = await res.json();
        setGlobalSettings(data.settings || {});
      }
    } catch (err) {
      console.error("Failed to fetch global settings:", err);
    }
  };

  const fetchTenantInstances = async (page = 1) => {
    setLoadingInstances(true);
    try {
      const res = await authFetch(`${API}/admin/tenant-automations/v2/tenant-instances?page=${page}&per_page=50`);
      if (res.ok) {
        const data = await res.json();
        setTenantInstances(data.instances || []);
        setInstancesTotal(data.total || 0);
        setInstancesPage(data.page || 1);
      }
    } catch (err) {
      console.error("Failed to fetch tenant instances:", err);
    } finally {
      setLoadingInstances(false);
    }
  };

  // Legacy fetchData for backwards compatibility
  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, automationsRes] = await Promise.all([
        authFetch(`${API}/admin/tenant-automations/settings`),
        authFetch(`${API}/admin/tenant-automations/v2/automations`),
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setGlobalSettings(data.settings || {});
      }

      if (automationsRes.ok) {
        const data = await automationsRes.json();
        setAutomations(data.automations || []);
      }
    } catch (err) {
      console.error("Failed to fetch automations data:", err);
      toast.error("Failed to load automations");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTemplates = async () => {
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/templates/active/list?template_type=automation`);
      if (res.ok) {
        const data = await res.json();
        setAvailableTemplates(data.templates || []);
      }
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    }
  };

  const previewSelectedTemplate = async (templateId) => {
    if (!templateId) {
      setTemplatePreviewData(null);
      return;
    }
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/templates/${templateId}/preview`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        setTemplatePreviewData(data);
        setShowTemplatePreview(true);
      }
    } catch (err) {
      console.error("Failed to preview template:", err);
    }
  };

  const fetchV2Enums = async () => {
    try {
      const res = await authFetch(`${API}/admin/tenant-automations/v2/enums`);
      if (res.ok) {
        const data = await res.json();
        setV2Enums(data.enums);
      }
    } catch (err) {
      console.error("Failed to fetch V2 enums:", err);
    }
  };

  // Update global settings
  const updateGlobalSettings = async (updates) => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/admin/tenant-automations/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });

      if (res.ok) {
        const data = await res.json();
        setGlobalSettings(data.settings);
        toast.success("Global settings updated");
      } else {
        toast.error("Failed to update settings");
      }
    } catch (err) {
      console.error("Failed to update settings:", err);
      toast.error("Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  // Toggle automation enabled
  const toggleAutomation = async (automationId, enabled) => {
    try {
      const res = await authFetch(`${API}/admin/tenant-automations/v2/automations/${automationId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });

      if (res.ok) {
        setAutomations((prev) =>
          prev.map((a) => (a.automation_id === automationId ? { ...a, enabled } : a))
        );
        toast.success(enabled ? "Automation enabled" : "Automation disabled");
      } else {
        toast.error("Failed to update automation");
      }
    } catch (err) {
      console.error("Failed to toggle automation:", err);
      toast.error("Failed to update automation");
    }
  };

  // Toggle automation mode
  const toggleMode = async (automationId, currentMode) => {
    const newMode = currentMode === "live" ? "dry_run" : "live";
    
    try {
      const res = await authFetch(`${API}/admin/tenant-automations/v2/automations/${automationId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: newMode }),
      });

      if (res.ok) {
        setAutomations((prev) =>
          prev.map((a) => (a.automation_id === automationId ? { ...a, mode: newMode } : a))
        );
        toast.success(`Mode changed to ${newMode === "live" ? "LIVE" : "Dry Run"}`);
      } else {
        toast.error("Failed to update mode");
      }
    } catch (err) {
      console.error("Failed to toggle mode:", err);
      toast.error("Failed to update mode");
    }
  };

  // Run automation cycle
  const runCycle = async () => {
    setRunningCycle(true);
    try {
      const res = await authFetch(`${API}/admin/tenant-automations/run-cycle`, {
        method: "POST",
      });

      if (res.ok) {
        const data = await res.json();
        const stats = data.stats || {};
        
        if (stats.skipped_reason === "global_disabled") {
          toast.info("Cycle skipped: Global automations are disabled");
        } else {
          toast.success(
            `Cycle complete: ${stats.sends_successful || 0} sent, ${stats.sends_skipped || 0} skipped, ${stats.errors || 0} errors`
          );
        }
        
        fetchData();
      } else {
        toast.error("Failed to run cycle");
      }
    } catch (err) {
      console.error("Failed to run cycle:", err);
      toast.error("Failed to run cycle");
    } finally {
      setRunningCycle(false);
    }
  };

  // ============================================================================
  // V2: PLATFORM-LEVEL SEEDING (NO TENANT SELECTION)
  // ============================================================================

  const seedAllAutomations = async () => {
    setSeeding(true);
    
    try {
      const res = await authFetch(`${API}/admin/tenant-automations/v2/automations/seed-all`, {
        method: "POST",
      });

      if (res.ok) {
        const data = await res.json();
        const summary = data.summary || {};
        const { tenants_scanned, tenants_updated, automations_created, tenants_already_complete } = summary;
        
        // Provide clear feedback based on outcome
        if (automations_created > 0) {
          toast.success(
            <div>
              <p className="font-medium">Created {automations_created} automation{automations_created !== 1 ? 's' : ''}</p>
              <p className="text-sm opacity-80">
                Updated {tenants_updated} tenant{tenants_updated !== 1 ? 's' : ''} • {tenants_already_complete} already complete
              </p>
            </div>,
            { duration: 6000 }
          );
        } else {
          toast.info(
            <div>
              <p className="font-medium">No new automations created</p>
              <p className="text-sm opacity-80">
                All {tenants_scanned} eligible tenant{tenants_scanned !== 1 ? 's' : ''} already have their defaults
              </p>
            </div>,
            { duration: 5000 }
          );
        }
        
        setSeedDialog(false);
        fetchData();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Failed to seed automations");
      }
    } catch (err) {
      console.error("Failed to seed automations:", err);
      toast.error("Failed to seed automations");
    } finally {
      setSeeding(false);
    }
  };

  // ============================================================================
  // V2: PREVIEW QUALIFICATION
  // ============================================================================

  const openPreviewDialog = async (automation) => {
    setPreviewDialog(true);
    setLoadingPreview(true);
    setPreviewData(null);

    try {
      const res = await authFetch(`${API}/admin/tenant-automations/v2/automations/${automation.automation_id}/preview?limit=10`);
      if (res.ok) {
        const data = await res.json();
        setPreviewData(data);
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Failed to load preview");
        setPreviewDialog(false);
      }
    } catch (err) {
      console.error("Failed to load preview:", err);
      toast.error("Failed to load preview");
      setPreviewDialog(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // ============================================================================
  // V2: TEST NOW (PER-AUTOMATION)
  // ============================================================================

  const testAutomationNow = async (automation) => {
    if (!globalSettings.preview_recipient_email) {
      toast.error("Set a Preview Recipient Email before using Test Now.", { duration: 5000 });
      return;
    }

    setTestingAutomation(automation.automation_id);
    setTestResult(null);

    try {
      const res = await authFetch(`${API}/admin/tenant-automations/v2/automations/${automation.automation_id}/test-now`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          force_dry_run: true,
          preview_email: globalSettings.preview_recipient_email,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setTestResult(data);
        
        if (data.sends_successful > 0) {
          const liveCount = data.evaluation_result?.live_qualifying_count || 0;
          toast.success(
            <div>
              <p className="font-medium">Test email sent!</p>
              <p className="text-sm opacity-80">
                Sent to {globalSettings.preview_recipient_email}
              </p>
              <p className="text-xs opacity-60 mt-1">
                Live qualifying: {liveCount} recipient{liveCount !== 1 ? 's' : ''}
              </p>
            </div>,
            { duration: 6000 }
          );
        } else if (data.errors?.length > 0) {
          toast.error(data.errors[0] || "Test failed", { duration: 5000 });
        } else {
          toast.warning("Test completed but no email was sent. Check server logs.", { duration: 5000 });
        }
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Test failed", { duration: 5000 });
      }
    } catch (err) {
      console.error("Failed to test automation:", err);
      toast.error("Test failed - network error", { duration: 5000 });
    } finally {
      setTestingAutomation(null);
    }
  };

  // ============================================================================
  // V2: EDIT AUTOMATION (supports both platform defaults and tenant instances)
  // ============================================================================

  const openEditDialog = (automation, isDefault = false) => {
    setEditingAutomation(automation);
    setIsPlatformDefault(isDefault);
    setEditForm({
      name: automation.name || "",
      enabled: automation.enabled || false,
      mode: automation.mode || "dry_run",
      delay_hours: automation.delay_hours || 48,
      cooldown_hours: automation.cooldown_hours || 168,
      trigger_event: automation.trigger_event || "signup",
      required_event: automation.required_event || "none",
      exit_event: automation.exit_event || "none",
      send_once_only: automation.send_once_only || false,
      max_sends_total: automation.max_sends_total || "",
      business_hours_only: automation.business_hours_only || false,
      template_key: automation.template_key || "",
      template_id: automation.template_id || "",
    });
    setTemplatePreviewData(null);
    setShowTemplatePreview(false);
    setEditDialog(true);
  };

  const saveEditForm = async () => {
    if (!editingAutomation) return;

    setSavingEdit(true);
    try {
      // Build update payload
      const updates = {
        name: editForm.name,
        enabled: editForm.enabled,
        mode: editForm.mode,
        delay_hours: parseInt(editForm.delay_hours) || 48,
        cooldown_hours: parseInt(editForm.cooldown_hours) || 168,
        trigger_event: editForm.trigger_event,
        required_event: editForm.required_event,
        exit_event: editForm.exit_event,
        send_once_only: editForm.send_once_only,
        business_hours_only: editForm.business_hours_only,
      };

      // Include template_id if selected
      if (editForm.template_id) {
        updates.template_id = editForm.template_id;
      }

      if (editForm.max_sends_total) {
        updates.max_sends_total = parseInt(editForm.max_sends_total);
      }

      // Use different endpoint for platform defaults
      const endpoint = isPlatformDefault
        ? `${API}/admin/tenant-automations/v2/platform-defaults/${editingAutomation.automation_id}`
        : `${API}/admin/tenant-automations/v2/automations/${editingAutomation.automation_id}`;

      const res = await authFetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });

      if (res.ok) {
        const data = await res.json();
        
        if (isPlatformDefault) {
          // Update platform defaults list
          setPlatformDefaults((prev) =>
            prev.map((a) =>
              a.automation_id === editingAutomation.automation_id ? data.automation : a
            )
          );
          toast.success(
            <div>
              <p className="font-medium">Platform default updated</p>
              <p className="text-sm opacity-80">Changes apply to future tenants only</p>
            </div>,
            { duration: 5000 }
          );
        } else {
          // Update automations list (legacy)
          setAutomations((prev) =>
            prev.map((a) =>
              a.automation_id === editingAutomation.automation_id ? data.automation : a
            )
          );
          toast.success("Automation updated");
        }
        
        setEditDialog(false);
        setEditingAutomation(null);
        setIsPlatformDefault(false);
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Failed to update automation");
      }
    } catch (err) {
      console.error("Failed to update automation:", err);
      toast.error("Failed to update automation");
    } finally {
      setSavingEdit(false);
    }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  const formatTriggerType = (type) => {
    const labels = {
      signup_no_customer: "Signup -> No Customer",
      customer_no_estimate: "Customer -> No Estimate",
      estimate_no_invoice: "Estimate -> No Invoice",
      inactivity: "Inactivity",
      first_success_upsell: "First Success -> Upsell",
    };
    return labels[type] || type;
  };

  const formatV2Event = (event) => {
    if (!event || event === "none") return "None";
    return event.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loadingDefaults) {
    return (
      <Card className="bg-zinc-900 border-zinc-700">
        <CardContent className="py-12">
          <div className="text-center text-zinc-400">
            <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
            <span className="text-zinc-300">Loading platform defaults...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Global Disabled Banner */}
      {!globalSettings.enabled_global && (
        <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-4 flex items-center gap-3">
          <Warning size={24} className="text-amber-500 flex-shrink-0" />
          <div>
            <p className="text-amber-100 font-medium">Automations are currently disabled</p>
            <p className="text-amber-200/70 text-sm">
              Enable the global switch below to start processing automations.
            </p>
          </div>
        </div>
      )}

      {/* Global Settings Card */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-zinc-100 flex items-center gap-2">
            <Gear size={20} className="text-[#FF6600]" />
            Global Settings
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Control automations system-wide
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-zinc-100">Global Enable</Label>
              <p className="text-zinc-400 text-sm">
                Master switch for all automations
              </p>
            </div>
            <Switch
              checked={globalSettings.enabled_global}
              onCheckedChange={(checked) => updateGlobalSettings({ enabled_global: checked })}
              disabled={saving}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-100">Preview Recipient Email</Label>
            <p className="text-zinc-400 text-sm">
              Dry-run and Test Now emails will be sent here instead of actual recipients
            </p>
            <div className="flex gap-2">
              <Input
                type="email"
                value={globalSettings.preview_recipient_email || ""}
                onChange={(e) =>
                  setGlobalSettings((prev) => ({
                    ...prev,
                    preview_recipient_email: e.target.value,
                  }))
                }
                placeholder="admin@example.com"
                className="bg-zinc-800 border-zinc-600 text-zinc-100 placeholder:text-zinc-500 flex-1"
              />
              <Button
                variant="outline"
                onClick={() =>
                  updateGlobalSettings({
                    preview_recipient_email: globalSettings.preview_recipient_email,
                  })
                }
                disabled={saving}
                className="border-zinc-600 text-zinc-100 hover:bg-zinc-800 hover:text-zinc-50"
              >
                Save
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-3 flex-wrap">
        <Button
          onClick={() => setSeedDialog(true)}
          variant="outline"
          className="border-zinc-600 text-zinc-100 hover:bg-zinc-800 hover:text-zinc-50"
        >
          <Robot size={18} className="mr-2" />
          Seed Default Automations
        </Button>
        <Button
          onClick={runCycle}
          disabled={runningCycle}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
        >
          {runningCycle ? (
            <ArrowClockwise size={18} className="mr-2 animate-spin" />
          ) : (
            <Play size={18} className="mr-2" />
          )}
          {runningCycle ? "Running..." : "Run Cycle Now"}
        </Button>
        <Button
          onClick={() => {
            fetchPlatformDefaults();
            fetchGlobalSettings();
          }}
          variant="ghost"
          className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
        >
          <ArrowClockwise size={18} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* ============================================================================
          PLATFORM DEFAULT AUTOMATIONS (PRIMARY VIEW)
          ============================================================================ */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-zinc-100 flex items-center gap-2">
                <Robot size={20} className="text-[#FF6600]" />
                Platform Default Automations
              </CardTitle>
              <CardDescription className="text-zinc-400 mt-1">
                Master templates copied to new tenants. Changes here affect <span className="text-emerald-400 font-medium">future tenants only</span>.
              </CardDescription>
            </div>
            <Badge className="bg-[#FF6600]/20 text-[#FF6600] border-[#FF6600]/30 px-3 py-1">
              Primary Model
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loadingDefaults ? (
            <div className="text-center py-8 text-zinc-400">
              <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
              <span className="text-zinc-300">Loading platform defaults...</span>
            </div>
          ) : platformDefaults.length === 0 ? (
            <div className="text-center py-8 text-zinc-400">
              <Robot size={40} className="mx-auto mb-3 opacity-50" />
              <p className="text-zinc-300">No platform defaults found</p>
              <p className="text-sm text-zinc-500 mt-2">Platform defaults will be created automatically</p>
              <Button
                onClick={fetchPlatformDefaults}
                className="mt-4 bg-[#FF6600] hover:bg-[#CC5200] text-white"
              >
                <ArrowClockwise size={16} className="mr-2" />
                Refresh
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-700 hover:bg-transparent">
                    <TableHead className="text-zinc-300">Default Name</TableHead>
                    <TableHead className="text-zinc-300">Trigger</TableHead>
                    <TableHead className="text-zinc-300">Initial Status</TableHead>
                    <TableHead className="text-zinc-300">Initial Mode</TableHead>
                    <TableHead className="text-zinc-300">Delay</TableHead>
                    <TableHead className="text-zinc-300 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {platformDefaults.map((auto) => (
                    <TableRow
                      key={auto.automation_id}
                      className="border-zinc-700 hover:bg-zinc-800/50"
                    >
                      <TableCell className="text-zinc-100 font-medium">
                        <div className="flex items-center gap-2">
                          {auto.name}
                          <Badge className="bg-purple-600/20 text-purple-400 border-purple-600/30 text-xs">
                            Default
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-300">
                        {formatTriggerType(auto.trigger_type)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={auto.enabled ? "default" : "secondary"}
                          className={
                            auto.enabled
                              ? "bg-green-600/20 text-green-400 border-green-600/30"
                              : "bg-zinc-700 text-zinc-400 border-zinc-600"
                          }
                        >
                          {auto.enabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {auto.mode === "live" ? (
                          <Badge className="bg-red-600/20 text-red-400 border-red-600/30">
                            LIVE
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30">
                            Dry Run
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-300">
                        {auto.delay_hours || 48}h
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(auto, true)}
                          className="h-8 px-3 text-zinc-300 border-zinc-600 hover:text-zinc-100 hover:bg-zinc-700 hover:border-zinc-500"
                        >
                          <PencilSimple size={14} className="mr-1.5" />
                          <span className="text-xs">Edit Default</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Info Box */}
          <div className="mt-6 bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
            <div className="flex items-start gap-3">
              <Info size={18} className="text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-zinc-400">
                <p className="font-medium text-zinc-300 mb-1">How Platform Defaults Work</p>
                <ul className="space-y-1">
                  <li>• When a <span className="text-emerald-400">new tenant signs up</span>, they automatically receive copies of these 5 defaults</li>
                  <li>• Changes you make here only affect <span className="text-amber-400">future tenants</span></li>
                  <li>• Existing tenants keep their current automations unchanged</li>
                  <li>• Use the backfill tool below to update existing tenants if needed</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============================================================================
          SECONDARY ADMIN TOOLS
          ============================================================================ */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-zinc-100 flex items-center gap-2">
            <Wrench size={20} className="text-zinc-400" />
            Admin Tools
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Backfill and recovery tools for existing tenants
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
            <div>
              <p className="text-zinc-200 font-medium">Initialize Missing Automations</p>
              <p className="text-zinc-400 text-sm mt-1">
                Create missing default automations for existing tenants. Will not overwrite existing automations or create duplicates.
              </p>
            </div>
            <Button
              onClick={() => setSeedDialog(true)}
              variant="outline"
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
            >
              <Robot size={16} className="mr-2" />
              Backfill Missing
            </Button>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
            <div>
              <p className="text-zinc-200 font-medium">View Tenant Automation Instances</p>
              <p className="text-zinc-400 text-sm mt-1">
                Browse all tenant-level automation copies for debugging and support.
              </p>
            </div>
            <Button
              onClick={() => {
                setShowTenantInstances(!showTenantInstances);
                if (!showTenantInstances) {
                  fetchTenantInstances(1);
                }
              }}
              variant="outline"
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
            >
              <Users size={16} className="mr-2" />
              {showTenantInstances ? "Hide Instances" : "View Instances"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ============================================================================
          TENANT INSTANCES (SECONDARY VIEW - Debug/Support)
          ============================================================================ */}
      {showTenantInstances && (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-zinc-100 flex items-center gap-2">
                  <Users size={20} className="text-zinc-400" />
                  Tenant Automation Instances
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  {instancesTotal} tenant-level automation{instancesTotal !== 1 ? "s" : ""} (debug view)
                </CardDescription>
              </div>
              <Badge className="bg-zinc-700 text-zinc-400 border-zinc-600">
                Secondary View
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {loadingInstances ? (
              <div className="text-center py-8 text-zinc-400">
                <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
                <span className="text-zinc-300">Loading instances...</span>
              </div>
            ) : tenantInstances.length === 0 ? (
              <div className="text-center py-8 text-zinc-400">
                <Users size={40} className="mx-auto mb-3 opacity-50" />
                <p className="text-zinc-300">No tenant instances found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-700 hover:bg-transparent">
                      <TableHead className="text-zinc-300">Tenant</TableHead>
                      <TableHead className="text-zinc-300">Name</TableHead>
                      <TableHead className="text-zinc-300">Trigger</TableHead>
                      <TableHead className="text-zinc-300">Status</TableHead>
                      <TableHead className="text-zinc-300">Mode</TableHead>
                      <TableHead className="text-zinc-300">Last Run</TableHead>
                      <TableHead className="text-zinc-300 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tenantInstances.map((auto) => (
                      <TableRow
                        key={auto.automation_id}
                        className="border-zinc-700 hover:bg-zinc-800/50"
                      >
                        <TableCell className="text-zinc-400 text-xs font-mono">
                          {auto.tenant_id?.replace("tenant_", "").substring(0, 8)}...
                        </TableCell>
                        <TableCell className="text-zinc-100 font-medium">
                          {auto.name}
                        </TableCell>
                        <TableCell className="text-zinc-300">
                          {formatTriggerType(auto.trigger_type)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={auto.enabled ? "default" : "secondary"}
                            className={
                              auto.enabled
                                ? "bg-green-600/20 text-green-400 border-green-600/30"
                                : "bg-zinc-700 text-zinc-400 border-zinc-600"
                            }
                          >
                            {auto.enabled ? "On" : "Off"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={auto.mode === "live" ? "bg-red-600/20 text-red-400 border-red-600/30" : "bg-blue-600/20 text-blue-400 border-blue-600/30"}>
                            {auto.mode === "live" ? "LIVE" : "Dry"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-zinc-400 text-sm">
                          {formatDate(auto.last_run_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPreviewDialog(auto)}
                              className="h-7 px-2 text-zinc-400 border-zinc-600 hover:text-zinc-100 hover:bg-zinc-700"
                            >
                              <Eye size={12} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(auto, false)}
                              className="h-7 px-2 text-zinc-400 border-zinc-600 hover:text-zinc-100 hover:bg-zinc-700"
                            >
                              <PencilSimple size={12} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============================================================================
          SEED DIALOG - Platform-Level Simple Confirmation (No Tenant Selection)
          ============================================================================ */}
      <Dialog open={seedDialog} onOpenChange={setSeedDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Robot size={20} className="text-[#FF6600]" />
              Seed Missing Default Automations
            </DialogTitle>
            <DialogDescription className="text-zinc-400 leading-relaxed">
              This will create any missing default automations for <span className="text-zinc-200 font-medium">all eligible tenants</span>.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Info Cards */}
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50 space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle size={18} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                <p className="text-zinc-300 text-sm">
                  Existing defaults will <span className="text-emerald-400 font-medium">not</span> be duplicated
                </p>
              </div>
              <div className="flex items-start gap-3">
                <PauseCircle size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-zinc-300 text-sm">
                  New automations are created <span className="text-amber-400 font-medium">disabled</span> and in <span className="text-blue-400 font-medium">dry-run mode</span>
                </p>
              </div>
              <div className="flex items-start gap-3">
                <ArrowClockwise size={18} className="text-zinc-400 mt-0.5 flex-shrink-0" />
                <p className="text-zinc-300 text-sm">
                  Safe to run multiple times
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-3 sm:gap-3 pt-2 border-t border-zinc-700">
            <Button
              variant="outline"
              onClick={() => setSeedDialog(false)}
              disabled={seeding}
              className="border-zinc-600 text-zinc-100 hover:bg-zinc-800 h-10"
            >
              Cancel
            </Button>
            <Button
              onClick={seedAllAutomations}
              disabled={seeding}
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white h-10 px-6"
            >
              {seeding ? (
                <>
                  <ArrowClockwise size={16} className="mr-2 animate-spin" />
                  Seeding...
                </>
              ) : (
                "Seed Missing Defaults"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================================
          PREVIEW DIALOG - V2 Qualification Preview
          ============================================================================ */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Eye size={20} className="text-[#FF6600]" />
              Preview Qualifying Recipients
            </DialogTitle>
          </DialogHeader>
          
          {loadingPreview ? (
            <div className="py-8 text-center">
              <ArrowClockwise size={24} className="animate-spin mx-auto mb-2 text-zinc-400" />
              <p className="text-zinc-400">Evaluating automation...</p>
            </div>
          ) : previewData ? (
            <div className="space-y-4">
              {/* Count */}
              <div className="bg-zinc-800 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-[#FF6600]">{previewData.eligible_count}</div>
                <div className="text-zinc-400 text-sm">Qualifying Recipients</div>
              </div>
              
              {/* Match Reasons */}
              {previewData.match_reasons?.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-zinc-300 text-sm">Match Criteria</Label>
                  <div className="bg-zinc-800 rounded-lg p-3 space-y-1">
                    {previewData.match_reasons.map((reason, i) => (
                      <p key={i} className="text-zinc-300 text-sm flex items-center gap-2">
                        <CheckCircle size={14} className="text-green-400" />
                        {reason}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Sample Recipients */}
              {previewData.sample_recipients?.length > 0 ? (
                <div className="space-y-1">
                  <Label className="text-zinc-300 text-sm">Sample Recipients (up to 10)</Label>
                  <div className="bg-zinc-800 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                    {previewData.sample_recipients.map((recipient, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Envelope size={14} className="text-zinc-500" />
                        <span className="text-zinc-100">{recipient.name || "Unknown"}</span>
                        <span className="text-zinc-500">-</span>
                        <span className="text-zinc-400">{recipient.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-zinc-800 rounded-lg p-4 text-center">
                  <p className="text-zinc-400">No qualifying recipients found</p>
                  <p className="text-zinc-500 text-sm mt-1">
                    This automation has no matching targets at this time
                  </p>
                </div>
              )}
              
              <p className="text-zinc-500 text-xs">
                Evaluated at: {formatDate(previewData.evaluated_at)}
              </p>
            </div>
          ) : null}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPreviewDialog(false)}
              className="border-zinc-600 text-zinc-100 hover:bg-zinc-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================================
          EDIT DIALOG - V2 Constrained Fields (Improved Layout)
          ============================================================================ */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {/* Header - Sticky */}
          <div className="sticky top-0 z-10 bg-zinc-900 border-b border-zinc-700 px-6 py-5">
            <DialogHeader className="space-y-1">
              <DialogTitle className="text-zinc-100 flex items-center gap-2 text-lg">
                <PencilSimple size={22} className="text-[#FF6600]" />
                Edit Automation
              </DialogTitle>
              <DialogDescription className="text-zinc-400 text-sm">
                Update automation settings. Changes take effect immediately.
              </DialogDescription>
            </DialogHeader>
          </div>
          
          {editingAutomation && (
            <div className="px-6 py-6 space-y-6">
              
              {/* ═══════════════════════════════════════════════════════════════════
                  SECTION 1: Basic Settings
                  ═══════════════════════════════════════════════════════════════════ */}
              <div className="bg-zinc-800/50 rounded-xl p-5 space-y-5 border border-zinc-700/50">
                <div className="flex items-center gap-2 pb-2 border-b border-zinc-700/50">
                  <Gear size={16} className="text-[#FF6600]" />
                  <span className="text-zinc-200 font-medium text-sm">Basic Settings</span>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <Label className="text-zinc-200 text-sm font-medium">Automation Name</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-600 text-zinc-100 h-10"
                  />
                </div>

                {/* Status & Mode Row */}
                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-zinc-200 text-sm font-medium">Status</Label>
                    <Select
                      value={editForm.enabled ? "enabled" : "disabled"}
                      onValueChange={(val) => setEditForm({ ...editForm, enabled: val === "enabled" })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-600 text-zinc-100 h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-600">
                        <SelectItem value="disabled" className="text-zinc-100">Disabled</SelectItem>
                        <SelectItem value="enabled" className="text-zinc-100">Enabled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-200 text-sm font-medium">Mode</Label>
                    <Select
                      value={editForm.mode}
                      onValueChange={(val) => setEditForm({ ...editForm, mode: val })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-600 text-zinc-100 h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-600">
                        <SelectItem value="dry_run" className="text-zinc-100">Dry Run</SelectItem>
                        <SelectItem value="live" className="text-zinc-100">Live</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* ═══════════════════════════════════════════════════════════════════
                  SECTION 2: Trigger & Exit Logic
                  ═══════════════════════════════════════════════════════════════════ */}
              <div className="bg-zinc-800/50 rounded-xl p-5 space-y-5 border border-zinc-700/50">
                <div className="flex items-center gap-2 pb-2 border-b border-zinc-700/50">
                  <Lightning size={16} className="text-amber-500" />
                  <span className="text-zinc-200 font-medium text-sm">Trigger & Exit Logic</span>
                </div>

                {/* Trigger & Required Event Row */}
                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-zinc-200 text-sm font-medium">Trigger Event</Label>
                    <p className="text-zinc-500 text-xs -mt-1">When this happens...</p>
                    <Select
                      value={editForm.trigger_event}
                      onValueChange={(val) => setEditForm({ ...editForm, trigger_event: val })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-600 text-zinc-100 h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-600">
                        {(v2Enums?.trigger_events || ["signup", "first_customer", "first_estimate", "first_invoice", "last_activity", "first_success"]).map((event) => (
                          <SelectItem key={event} value={event} className="text-zinc-100">
                            {formatV2Event(event)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-200 text-sm font-medium">Required Missing</Label>
                    <p className="text-zinc-500 text-xs -mt-1">...and this hasn't occurred</p>
                    <Select
                      value={editForm.required_event}
                      onValueChange={(val) => setEditForm({ ...editForm, required_event: val })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-600 text-zinc-100 h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-600">
                        {(v2Enums?.required_events || ["none", "customer_created", "estimate_created", "invoice_created", "payment_received", "payment_processing_purchased"]).map((event) => (
                          <SelectItem key={event} value={event} className="text-zinc-100">
                            {formatV2Event(event)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Exit Event */}
                <div className="space-y-2">
                  <Label className="text-zinc-200 text-sm font-medium">Exit Event</Label>
                  <p className="text-zinc-500 text-xs -mt-1">Stop sending if this occurs</p>
                  <Select
                    value={editForm.exit_event}
                    onValueChange={(val) => setEditForm({ ...editForm, exit_event: val })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-600 text-zinc-100 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-600">
                      {(v2Enums?.exit_events || ["none", "customer_created", "estimate_created", "invoice_created", "payment_received", "payment_processing_purchased"]).map((event) => (
                        <SelectItem key={event} value={event} className="text-zinc-100">
                          {formatV2Event(event)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* ═══════════════════════════════════════════════════════════════════
                  SECTION 3: Timing
                  ═══════════════════════════════════════════════════════════════════ */}
              <div className="bg-zinc-800/50 rounded-xl p-5 space-y-5 border border-zinc-700/50">
                <div className="flex items-center gap-2 pb-2 border-b border-zinc-700/50">
                  <Clock size={16} className="text-blue-400" />
                  <span className="text-zinc-200 font-medium text-sm">Timing</span>
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-zinc-200 text-sm font-medium">Delay (hours)</Label>
                    <p className="text-zinc-500 text-xs -mt-1">Wait before sending</p>
                    <Input
                      type="number"
                      min="1"
                      value={editForm.delay_hours}
                      onChange={(e) => setEditForm({ ...editForm, delay_hours: e.target.value })}
                      className="bg-zinc-800 border-zinc-600 text-zinc-100 h-10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-200 text-sm font-medium">Cooldown (hours)</Label>
                    <p className="text-zinc-500 text-xs -mt-1">Min gap between re-sends</p>
                    <Input
                      type="number"
                      min="0"
                      value={editForm.cooldown_hours}
                      onChange={(e) => setEditForm({ ...editForm, cooldown_hours: e.target.value })}
                      className="bg-zinc-800 border-zinc-600 text-zinc-100 h-10"
                    />
                  </div>
                </div>
              </div>

              {/* ═══════════════════════════════════════════════════════════════════
                  SECTION 4: Send Rules
                  ═══════════════════════════════════════════════════════════════════ */}
              <div className="bg-zinc-800/50 rounded-xl p-5 space-y-5 border border-zinc-700/50">
                <div className="flex items-center gap-2 pb-2 border-b border-zinc-700/50">
                  <Envelope size={16} className="text-green-400" />
                  <span className="text-zinc-200 font-medium text-sm">Send Rules</span>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div>
                    <Label className="text-zinc-200 text-sm font-medium">Send Once Only</Label>
                    <p className="text-zinc-500 text-xs mt-0.5">Only send to each recipient once ever</p>
                  </div>
                  <Switch
                    checked={editForm.send_once_only}
                    onCheckedChange={(checked) => setEditForm({ ...editForm, send_once_only: checked })}
                  />
                </div>

                <div className="flex items-center justify-between py-2 border-t border-zinc-700/30">
                  <div>
                    <Label className="text-zinc-200 text-sm font-medium">Business Hours Only</Label>
                    <p className="text-zinc-500 text-xs mt-0.5">Only send during 9am-5pm local time</p>
                  </div>
                  <Switch
                    checked={editForm.business_hours_only}
                    onCheckedChange={(checked) => setEditForm({ ...editForm, business_hours_only: checked })}
                  />
                </div>

                <div className="space-y-2 pt-2 border-t border-zinc-700/30">
                  <Label className="text-zinc-200 text-sm font-medium">Max Sends Total</Label>
                  <p className="text-zinc-500 text-xs -mt-1">Leave blank for unlimited</p>
                  <Input
                    type="number"
                    min="1"
                    value={editForm.max_sends_total}
                    onChange={(e) => setEditForm({ ...editForm, max_sends_total: e.target.value })}
                    placeholder="Unlimited"
                    className="bg-zinc-800 border-zinc-600 text-zinc-100 placeholder:text-zinc-500 h-10"
                  />
                </div>
              </div>

              {/* ═══════════════════════════════════════════════════════════════════
                  SECTION 5: Email Template
                  ═══════════════════════════════════════════════════════════════════ */}
              <div className="bg-zinc-800/50 rounded-xl p-5 space-y-5 border border-zinc-700/50">
                <div className="flex items-center gap-2 pb-2 border-b border-zinc-700/50">
                  <FileText size={16} className="text-purple-400" />
                  <span className="text-zinc-200 font-medium text-sm">Email Template</span>
                </div>
                
                <div className="space-y-3">
                  {/* FIX 2: Explicit template display - no more "Use default template" ambiguity */}
                  {(() => {
                    // Determine the active template display name
                    const systemTemplateNames = {
                      "automation_signup_nudge": "Signup Nudge",
                      "automation_estimate_nudge": "Estimate Nudge",
                      "automation_invoice_nudge": "Invoice Nudge",
                      "automation_inactivity_nudge": "Inactivity Reminder",
                      "automation_upsell": "Upsell Offer",
                    };
                    const systemTemplateName = editForm.template_key 
                      ? systemTemplateNames[editForm.template_key] || editForm.template_key.replace(/automation_|_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim()
                      : null;
                    const hasCustomTemplate = editForm.template_id && editForm.template_id !== "__default__";
                    const selectedCustomTemplate = hasCustomTemplate 
                      ? availableTemplates.find(t => t.template_id === editForm.template_id)
                      : null;
                    
                    return (
                      <>
                        {/* Current Template Indicator */}
                        <div className="bg-zinc-900/80 rounded-lg p-4 border border-zinc-600/50">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-[#FF6600]/20 flex items-center justify-center flex-shrink-0">
                              <Envelope size={16} className="text-[#FF6600]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-zinc-400 text-xs font-medium uppercase tracking-wide">Email that will be sent</p>
                              <p className="text-zinc-100 font-medium mt-1">
                                {hasCustomTemplate && selectedCustomTemplate
                                  ? `Custom: ${selectedCustomTemplate.name}`
                                  : systemTemplateName
                                    ? `System: ${systemTemplateName}`
                                    : "No template configured"
                                }
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Template Selector */}
                        <div className="space-y-2">
                          <Label className="text-zinc-300 text-sm">
                            {availableTemplates.length > 0 
                              ? "Override with custom template (optional)"
                              : "Custom template"
                            }
                          </Label>
                          <div className="flex items-center gap-2">
                            <Select
                              value={editForm.template_id || "__system__"}
                              onValueChange={(val) => setEditForm({ ...editForm, template_id: val === "__system__" ? "" : val })}
                            >
                              <SelectTrigger className="bg-zinc-800 border-zinc-600 text-zinc-100 h-10 flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-600">
                                <SelectItem value="__system__" className="text-zinc-200">
                                  {systemTemplateName 
                                    ? `Keep system template: ${systemTemplateName}`
                                    : "No custom template"
                                  }
                                </SelectItem>
                                {availableTemplates.length > 0 && (
                                  <>
                                    <div className="px-2 py-1.5 text-xs text-zinc-500 border-t border-zinc-700 mt-1">
                                      Custom Templates
                                    </div>
                                    {availableTemplates.map((tmpl) => (
                                      <SelectItem key={tmpl.template_id} value={tmpl.template_id} className="text-zinc-100">
                                        {tmpl.name}
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                            {hasCustomTemplate && selectedCustomTemplate && (
                              <>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => previewSelectedTemplate(editForm.template_id)}
                                  className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 h-10 px-3"
                                >
                                  <Eye size={14} className="mr-1.5" />
                                  Preview
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/admin/tenant-communications/templates/${editForm.template_id}?tab=automations`)}
                                  className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 h-10 px-3"
                                  title="Open template in editor"
                                >
                                  <ArrowSquareOut size={14} />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Template Preview Inline */}
                {showTemplatePreview && templatePreviewData && (
                  <div className="bg-zinc-900/80 rounded-lg p-4 space-y-3 border border-zinc-600/50 mt-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-zinc-300 text-sm font-medium">Template Preview</Label>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowTemplatePreview(false)}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-100"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <p className="text-zinc-300 text-sm">
                        <span className="text-zinc-500">Subject:</span> {templatePreviewData.rendered_subject}
                      </p>
                      <div className="bg-white rounded-lg p-4 text-sm max-h-40 overflow-y-auto">
                        <div dangerouslySetInnerHTML={{ __html: templatePreviewData.rendered_body }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Rule Summary (if available) */}
              {editingAutomation.rule_summary && (
                <div className="bg-zinc-800/30 rounded-xl p-4 border border-zinc-700/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Robot size={14} className="text-zinc-500" />
                    <Label className="text-zinc-400 text-xs font-medium uppercase tracking-wide">Rule Summary</Label>
                  </div>
                  <p className="text-zinc-300 text-sm leading-relaxed">{editingAutomation.rule_summary}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Footer - Sticky */}
          <div className="sticky bottom-0 bg-zinc-900 border-t border-zinc-700 px-6 py-4">
            <DialogFooter className="gap-3 sm:gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setEditDialog(false);
                  setEditingAutomation(null);
                }}
                className="border-zinc-600 text-zinc-100 hover:bg-zinc-800 h-10 px-5"
              >
                Cancel
              </Button>
              <Button
                onClick={saveEditForm}
                disabled={savingEdit}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white h-10 px-6"
              >
                {savingEdit ? (
                  <>
                    <ArrowClockwise size={16} className="mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AutomationsTab;
